import React, { useState, useRef, useEffect } from 'react';
import './accordion.scss';

// ChevronDown icon component
const ChevronDown = ({ className = '' }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

// Individual AccordionItem component
const AccordionItem = ({
  children,
  value,
  isOpen,
  onToggle,
  disabled = false,
  className = '',
}) => {
  return (
    <div
      className={`accordion-item ${className} ${disabled ? 'disabled' : ''}`}
    >
      {children}
    </div>
  );
};

// AccordionTrigger component
const AccordionTrigger = ({
  children,
  onClick,
  isOpen,
  disabled = false,
  className = '',
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <button
      className={`accordion-trigger ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      aria-expanded={isOpen}
      type="button"
    >
      <span className="accordion-trigger-text">{children}</span>
      <ChevronDown className={`accordion-chevron ${isOpen ? 'open' : ''}`} />
    </button>
  );
};

// AccordionContent component
const AccordionContent = ({ children, isOpen, className = '' }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, children]);

  return (
    <div
      className={`accordion-content ${className}`}
      style={{ height: `${height}px` }}
      aria-hidden={!isOpen}
    >
      <div ref={contentRef} className="accordion-content-inner">
        {children}
      </div>
    </div>
  );
};

// Main Accordion component
const Accordion = ({
  children,
  type = 'single', // 'single' or 'multiple'
  collapsible = false,
  defaultValue = null,
  value: controlledValue,
  onValueChange,
  className = '',
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState(
    type === 'multiple' ? defaultValue || [] : defaultValue
  );

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleValueChange = (itemValue) => {
    if (disabled) return;

    let newValue;

    if (type === 'multiple') {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      if (currentArray.includes(itemValue)) {
        newValue = currentArray.filter((v) => v !== itemValue);
      } else {
        newValue = [...currentArray, itemValue];
      }
    } else {
      // Single mode
      if (currentValue === itemValue && collapsible) {
        newValue = null;
      } else if (currentValue === itemValue && !collapsible) {
        return; // Don't allow closing if not collapsible
      } else {
        newValue = itemValue;
      }
    }

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onValueChange?.(newValue);
  };

  const isItemOpen = (itemValue) => {
    if (type === 'multiple') {
      return Array.isArray(currentValue) && currentValue.includes(itemValue);
    }
    return currentValue === itemValue;
  };

  const enhancedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child) && child.type === AccordionItem) {
      const itemValue = child.props.value || index.toString();
      const isOpen = isItemOpen(itemValue);

      return React.cloneElement(child, {
        isOpen,
        onToggle: () => handleValueChange(itemValue),
        children: React.Children.map(child.props.children, (grandChild) => {
          if (React.isValidElement(grandChild)) {
            if (grandChild.type === AccordionTrigger) {
              return React.cloneElement(grandChild, {
                isOpen,
                onClick: () => handleValueChange(itemValue),
                disabled: disabled || grandChild.props.disabled,
              });
            }
            if (grandChild.type === AccordionContent) {
              return React.cloneElement(grandChild, {
                isOpen,
              });
            }
          }
          return grandChild;
        }),
      });
    }
    return child;
  });

  return (
    <div className={`accordion ${className} ${disabled ? 'disabled' : ''}`}>
      {enhancedChildren}
    </div>
  );
};

// Compound component exports
Accordion.Item = AccordionItem;
Accordion.Trigger = AccordionTrigger;
Accordion.Content = AccordionContent;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
export default Accordion;
